/**
 * Guest Device Fingerprinting
 * 
 * Generates a unique fingerprint for guest users based on browser characteristics.
 * Used to identify returning guests without requiring account creation.
 */

/**
 * Generate SHA256 hash using Web Crypto API
 * 
 * @param message String to hash
 * @returns Promise<string> Hex string of hash
 */
async function sha256(message: string): Promise<string> {
  const msgBuffer = new TextEncoder().encode(message);
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  return hashHex;
}

/**
 * Generate a device fingerprint from browser characteristics
 * 
 * @returns Promise<string> SHA256 hash of device characteristics
 */
export async function generateDeviceFingerprint(): Promise<string> {
  try {
    const components: string[] = [];

    // User Agent
    components.push(navigator.userAgent || '');

    // Language
    components.push(navigator.language || '');

    // Screen resolution and color depth
    components.push(`${window.screen.width}x${window.screen.height}x${window.screen.colorDepth}`);

    // Timezone offset
    components.push(new Date().getTimezoneOffset().toString());

    // Platform
    components.push(navigator.platform || '');

    // Hardware concurrency (CPU cores)
    components.push((navigator.hardwareConcurrency || 0).toString());

    // Device memory (if available)
    const deviceMemory = (navigator as any).deviceMemory;
    if (deviceMemory) {
      components.push(deviceMemory.toString());
    }

    // Touch support
    const maxTouchPoints = navigator.maxTouchPoints || 0;
    components.push(maxTouchPoints.toString());

    // Combine all components
    const combined = components.join('|');
    console.log('[Fingerprint] Components:', combined);
    
    // Generate SHA256 hash using Web Crypto API
    const fingerprint = await sha256(combined);
    console.log('[Fingerprint] Generated hash:', fingerprint);
    
    return fingerprint;
  } catch (error) {
    console.error('[Fingerprint] Error generating fingerprint:', error);
    throw error;
  }
}

/**
 * Get stored guest token from localStorage
 * 
 * @returns string | null Guest token if exists
 */
export function getStoredGuestToken(): string | null {
  try {
    return localStorage.getItem('tidy_guest_token');
  } catch (e) {
    console.error('Failed to get guest token from localStorage:', e);
    return null;
  }
}

/**
 * Store guest token in localStorage
 * 
 * @param token Guest JWT token
 */
export function storeGuestToken(token: string): void {
  try {
    localStorage.setItem('tidy_guest_token', token);
  } catch (e) {
    console.error('Failed to store guest token in localStorage:', e);
  }
}

/**
 * Remove guest token from localStorage
 */
export function clearGuestToken(): void {
  try {
    localStorage.removeItem('tidy_guest_token');
  } catch (e) {
    console.error('Failed to clear guest token from localStorage:', e);
  }
}

/**
 * Get stored guest user ID from localStorage
 * 
 * @returns number | null Guest user ID if exists
 */
export function getStoredGuestUserId(): number | null {
  try {
    const userId = localStorage.getItem('tidy_guest_user_id');
    return userId ? parseInt(userId, 10) : null;
  } catch (e) {
    console.error('Failed to get guest user ID from localStorage:', e);
    return null;
  }
}

/**
 * Store guest user ID in localStorage
 * 
 * @param userId Guest user ID
 */
export function storeGuestUserId(userId: number): void {
  try {
    localStorage.setItem('tidy_guest_user_id', userId.toString());
  } catch (e) {
    console.error('Failed to store guest user ID in localStorage:', e);
  }
}

/**
 * Clear all guest data from localStorage
 */
export function clearGuestData(): void {
  clearGuestToken();
  try {
    localStorage.removeItem('tidy_guest_user_id');
    localStorage.removeItem('tidy_guest_name');
    localStorage.removeItem('tidy_device_fingerprint');
  } catch (e) {
    console.error('Failed to clear guest data from localStorage:', e);
  }
}

// isGuest function removed - not used. Check git history if needed

/**
 * Get or generate device fingerprint
 * Persists fingerprint in localStorage to maintain consistency across sessions
 * This is critical for iPhone Safari where browser characteristics may vary
 * 
 * @returns Promise<string> Device fingerprint
 */
let cachedFingerprint: string | null = null;

export async function getOrGenerateFingerprint(): Promise<string> {
  // Check memory cache first
  if (cachedFingerprint) {
    return cachedFingerprint;
  }
  
  // Check localStorage for persisted fingerprint
  try {
    const stored = localStorage.getItem('tidy_device_fingerprint');
    if (stored) {
      console.log('[Fingerprint] Using stored fingerprint from localStorage');
      cachedFingerprint = stored;
      return stored;
    }
  } catch (e) {
    console.warn('[Fingerprint] Failed to read from localStorage:', e);
  }
  
  // Generate new fingerprint
  console.log('[Fingerprint] Generating new fingerprint');
  const fingerprint = await generateDeviceFingerprint();
  cachedFingerprint = fingerprint;
  
  // Persist to localStorage for future sessions
  try {
    localStorage.setItem('tidy_device_fingerprint', fingerprint);
    console.log('[Fingerprint] Stored fingerprint in localStorage');
  } catch (e) {
    console.warn('[Fingerprint] Failed to store fingerprint in localStorage:', e);
  }
  
  return fingerprint;
}
